import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { UserAlertService } from '../../services/user-alert.service';
import { UserAlert } from '../../models/user-alert';
import { AdminPageHeaderComponent } from '../shared/admin-page-header/admin-page-header.component';
import { AdminStatusBadgeComponent } from '../shared/admin-status-badge/admin-status-badge.component';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

@Component({
  selector: 'app-user-info',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule,
    TooltipModule,
    DialogModule,
    AdminPageHeaderComponent,
    AdminStatusBadgeComponent,
  ],
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
})
export class UserInfoComponent implements OnDestroy {
  readonly title = 'Usuários';
  readonly subtitle = 'Monitore contas, alertas e status de limitação em um único painel.';
  users: UserAlert[] = [];
  isLoading = false;
  searchTerm = '';
  hasAlert = false;
  isLimited = false;
  isCreateDialogVisible = false;
  isCreatingUser = false;
  createUserError = '';
  createUserForm = {
    email: '',
    document_number: '',
    birthdate: '',
    password: '',
    confirm_password: '',
    skip_document_validation: false,
  };

  totalItems = 0;
  page = 1;
  rowsPerPage = 10;
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private userAlertService: UserAlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchInput$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((term) => {
        this.searchTerm = term;
        this.page = 1;
        this.updateQueryParams();
      });

    this.route.queryParams.subscribe(params => {
      this.searchTerm = params['search'] || '';
      this.hasAlert = params['has_alert'] === 'true';
      this.isLimited = params['is_limited'] === 'true';
      this.page = parseInt(params['page'] || '1', 10);
      this.fetchUsers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchInputChange(term: string) {
    this.searchTerm = term;
    this.searchInput$.next(term.trim());
  }

  onSearchChange() {
    this.page = 1;
    this.searchTerm = this.searchTerm.trim();
    this.updateQueryParams();
  }

  onFilterChange() {
    this.page = 1;
    this.updateQueryParams();
  }

  onLazyLoad(event: TableLazyLoadEvent) {
    const first = event.first ?? 0;
    this.page = Math.floor(first / this.rowsPerPage) + 1;
    this.updateQueryParams();
  }

  updateQueryParams() {
    const queryParams: Record<string, string | number | boolean | null> = {
      page: this.page,
      search: this.searchTerm || null,
      has_alert: this.hasAlert || null,
      is_limited: this.isLimited || null,
    };

    const currentPage = this.route.snapshot.queryParamMap.get('page') ?? '1';
    const currentSearch = this.route.snapshot.queryParamMap.get('search');
    const currentHasAlert = this.route.snapshot.queryParamMap.get('has_alert');
    const currentIsLimited = this.route.snapshot.queryParamMap.get('is_limited');

    const targetSearch = queryParams['search'] === null ? null : String(queryParams['search']);
    const targetHasAlert = queryParams['has_alert'] === null ? null : String(queryParams['has_alert']);
    const targetIsLimited = queryParams['is_limited'] === null ? null : String(queryParams['is_limited']);

    if (
      currentPage === String(queryParams['page']) &&
      currentSearch === targetSearch &&
      currentHasAlert === targetHasAlert &&
      currentIsLimited === targetIsLimited
    ) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    });
  }

  clearFilters() {
    this.searchTerm = '';
    this.hasAlert = false;
    this.isLimited = false;
    this.page = 1;
    this.updateQueryParams();
  }

  openCreateUserDialog() {
    this.resetCreateUserForm();
    this.isCreateDialogVisible = true;
  }

  closeCreateUserDialog() {
    this.isCreateDialogVisible = false;
  }

  createUser() {
    this.createUserError = '';

    if (this.createUserForm.password !== this.createUserForm.confirm_password) {
      this.createUserError = 'Confirmação de senha incorreta.';
      return;
    }

    const payload = {
      ...this.createUserForm,
      document_number: this.createUserForm.document_number.replace(/\D/g, ''),
    };

    this.isCreatingUser = true;
    this.userAlertService.createUser(payload).subscribe({
      next: () => {
        this.isCreatingUser = false;
        this.closeCreateUserDialog();
        this.fetchUsers();
      },
      error: (error) => {
        this.isCreatingUser = false;
        this.createUserError = error?.error?.detail || 'Não foi possível criar o usuário.';
      },
    });
  }

  viewDetails(userId: number) {
    this.router.navigate(['/dashboard/users-info', userId]);
  }

  trackByUserId(_: number, user: UserAlert): number {
    return user.id;
  }

  getUserName(user: UserAlert): string {
    const firstName = user.first_name?.trim();
    const lastName = user.last_name?.trim();
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    return fullName || user.username;
  }

  getAlertPreview(user: UserAlert): string[] {
    return user.alerts.slice(0, 2);
  }

  fetchUsers() {
    this.isLoading = true;

    this.userAlertService
      .listUsers(this.searchTerm, {
        has_alert: this.hasAlert,
        is_limited: this.isLimited,
        page: this.page,
      })
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.users = response.results;
          this.totalItems = response.count;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private resetCreateUserForm() {
    this.createUserError = '';
    this.isCreatingUser = false;
    this.createUserForm = {
      email: '',
      document_number: '',
      birthdate: '',
      password: '',
      confirm_password: '',
      skip_document_validation: false,
    };
  }
}
